import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// ⚡ 非同步取得設備資料
export const fetchEquipment = createAsyncThunk(
  "/api/equipment/fetchEquipment",
  async (params, thunkAPI) => {
    try {
      const res = await axios.get("/api/equipment", {
        params,
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      if (res.data.error === 0) {
        return res.data.list || null;
      } else {
        return thunkAPI.rejectWithValue("取得設備資料失敗");
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ⚡ 非同步取得資訊資料
export const fetchInfoData = createAsyncThunk(
  "/api/info/fetchInfoData",
  async (params, thunkAPI) => {
    try {
      const res = await axios.get("/api/info/data", {
        params,
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      if (res.data.error === 0) {
        return res.data.data || null;
      } else {
        return thunkAPI.rejectWithValue("取得資訊資料失敗");
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ⚡ 非同步取得圖表資料
export const fetchChartData = createAsyncThunk(
  "/api/chart/fetchChartData",
  async (params, thunkAPI) => {
    try {
      const res = await axios.get("/api/chart/data", {
        params,
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      if (res.data.error === 0) {
        return res.data || null;
      } else {
        return thunkAPI.rejectWithValue("取得圖表資料失敗");
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const informationSlice = createSlice({
  name: "information",
  initialState: {
    equipmentDetail: null,
    equipmentLoading: false,
    equipmentError: null,

    infoData: null,
    infoLoading: false,
    infoError: null,

    // chartData 改成一個物件存多筆
    chartData: {}, // { "1": {...}, "5": {...}, "9": {...} }
    chartLoading: {},
    chartError: {},
  },
  reducers: {
    clearInfoData: (state) => {
      state.infoData = null;
      state.infoError = null;
    },
    clearChartData: (state, action) => {
      if (action.payload) {
        delete state.chartData[action.payload];
        delete state.chartError[action.payload];
        delete state.chartLoading[action.payload];
      } else {
        state.chartData = {};
        state.chartError = {};
        state.chartLoading = {};
      }
    },
    clearAllData: (state) => {
      state.equipmentDetail = null;
      state.infoData = null;
      state.chartData = {};
      state.equipmentError = null;
      state.infoError = null;
      state.chartError = {};
      state.chartLoading = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Equipment
      .addCase(fetchEquipment.pending, (state) => {
        state.equipmentLoading = true;
        state.equipmentError = null;
      })
      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.equipmentLoading = false;
        state.equipmentDetail = action.payload;
      })
      .addCase(fetchEquipment.rejected, (state, action) => {
        state.equipmentLoading = false;
        state.equipmentError = action.payload;
      })
      // Info
      .addCase(fetchInfoData.pending, (state) => {
        state.infoLoading = true;
        state.infoError = null;
      })
      .addCase(fetchInfoData.fulfilled, (state, action) => {
        state.infoLoading = false;
        state.infoData = action.payload;
      })
      .addCase(fetchInfoData.rejected, (state, action) => {
        state.infoLoading = false;
        state.infoError = action.payload;
      })
      // Chart
      .addCase(fetchChartData.pending, (state, action) => {
        const key = action.meta.arg.type; // 用 type 當 key
        state.chartLoading[key] = true;
        state.chartError[key] = null;
      })
      .addCase(fetchChartData.fulfilled, (state, action) => {
        const key = action.meta.arg.type;
        state.chartLoading[key] = false;
        state.chartData[key] = action.payload;
      })
      .addCase(fetchChartData.rejected, (state, action) => {
        const key = action.meta.arg.type;
        state.chartLoading[key] = false;
        state.chartError[key] = action.payload;
      });
  },
});

// 原有的 selectors
export const selectEquipmentDetail = (state) =>
  state.information.equipmentDetail;
export const selectEquipmentLoading = (state) =>
  state.information.equipmentLoading;
export const selectEquipmentError = (state) => state.information.equipmentError;

// Info Data selectors
export const selectInfoData = (state) => state.information.infoData;
export const selectInfoLoading = (state) => state.information.infoLoading;
export const selectInfoError = (state) => state.information.infoError;

// Chart Data selectors
// Chart Data selectors
export const selectChartDataByType = (type) => (state) =>
  state.information.chartData[type] || null;

export const selectChartLoadingByType = (type) => (state) =>
  state.information.chartLoading[type] || false;

export const selectChartErrorByType = (type) => (state) =>
  state.information.chartError[type] || null;

// 匯出 actions
export const { clearInfoData, clearChartData, clearAllData } =
  informationSlice.actions;

export default informationSlice.reducer;
